<template lang="pug">
.briq-form-element.briq-date-time-picker(v-show="showInput")
  label.input-label.type-bold(:for="name" v-if="label") {{ label }} 
  briq-required-and-optional-markers(:isRequired="isRequired" :hideOptionalMarker="hideOptionalMarker" :label="label")
  BriqTooltip(:help="help")
  .flex.justify-center.items-center
    flat-pickr.briq-date-time-picker-input(
      :id="id"
      :placeholder="placeholder"
      :name="name"
      :config="flatPickrConfig"
      v-model="computedDate"
      v-bind="$attrs"
      ref="flatpicker"
      )
    span.clear-button(title="clear" data-clear @click="clearDate()" v-if="showClearButton")
      BriqIcon(name="icon-input-trash-icon")
  .validation-message(v-if="error" data-test-id="briq-datetime-picker-input-error") {{ error }}
</template>

<script>
import { mapGetters } from 'vuex'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import {
  getLocaleDateFormatStringForFlatPickr,
  convertDateFormatStringToFlatPickrDateFormat,
} from '@/helpers/dateTimeFormatter.js'
import 'flatpickr/dist/flatpickr.css'

export default {
  name: 'BriqDateTimePicker',
  components: {
    flatPickr,
  },
  props: {
    id: { type: [String, Number] },
    name: String,
    placeholder: { type: String, default: 'Select date' },
    label: String,
    hideOptionalMarker: Boolean,
    help: String,
    config: Object,
    value: String,
    showClearButton: Boolean,
    error: String,
    showInput: { type: Boolean, default: true },
    positionCalendarToElement: String,
  },
  computed: {
    ...mapGetters('briqState', ['businessUnitDataCulture']),
    defaultDateFormatStringForFlatPickr() {
      return convertDateFormatStringToFlatPickrDateFormat(this.$defaultDateFormat)
    },
    localeDateFormatStringForFlatPickr() {
      return getLocaleDateFormatStringForFlatPickr(this.businessUnitDataCulture || this.$locale)
    },
    flatPickrConfig() {
      return {
        dateFormat: this.config?.dateFormat || this.defaultDateFormatStringForFlatPickr,
        altFormat: this.config?.altFormat || this.localeDateFormatStringForFlatPickr,
        altInput: this.config?.altInput || true,
        altInputClass: this.config?.altInputClass || '',
        allowInput: this.config?.allowInput || false,
        ariaDateFormat: this.config?.altFormat || this.defaultDateFormatStringForFlatPickr,
        defaultDate: this.config?.defaultDate || null,
        maxDate: this.config?.maxDate || null,
        minDate: this.config?.minDate || null,
        monthSelectorType: this.config?.monthSelectorType || 'dropdown',
        parseDate: this.config?.parseDate || false,
        position: this.config?.position || 'auto',
        positionElement: this.config?.positionElement || null,
        showMonths: this.config?.showMonths || 1,
        wrap: this.config?.wrap || false,
      }
    },
    isRequired() {
      return this.$attrs.required || this.required
    },
    computedDate: {
      get() {
        return this.value
      },
      set(newValue) {
        if (newValue !== this.value) this.$emit('input', newValue)
      },
    },
  },
  methods: {
    clearDate() {
      this.computedDate = null
    },
  },
  mounted() {
    this.$parent.$on('openDatePicker', () => {
      const flatPickrInstance = this.$refs.flatpicker.fp
      if (this.positionCalendarToElement)
        flatPickrInstance._positionElement = this.$window.document.getElementById(
          this.positionCalendarToElement,
        )
      flatPickrInstance.open()
    })
  },
}
</script>

<style lang="stylus" scoped>
@import '~@v/variables.styl'

.clear-button {
  cursor: pointer;
  padding: 4px;
  margin: 0 space-XXS;
  svg {
    width: 16px;
    height: 16px;
    color: #4e5a6b;
  }
}

.briq-date-time-picker
  width 100%
  position relative
</style>

<style lang="stylus">
@import '~@v/variables.styl'

.briq-date-time-picker-input
  position relative
  width 100%
  border solid 1px #ebebeb
  border-radius 3px
  background white
  color #2c333a
  padding 7px

  &[disabled]
    background #eee
    cursor not-allowed

  &::placeholder
    color fade(@color-text, 40%)

  &:hover
    border-color #d3d3d3

  &:focus, &:active
    outline 0
    border-color #009BFF
    box-shadow 0 2px 4px 0 rgba(0, 0, 0, .15)

.flatpickr-calendar
  select
    border inherit
    font-size inherit
    font-weight inherit
</style>
